/**
 * <TutorCandidateHeader> — l'en-tête d'un compte candidat (organism).
 *
 * La coquille d'une candidature est légère : un parcours de recrutement, pas l'espace d'un tuteur
 * installé. Aujourd'hui, l'en-tête de la route `menu` (`TutorMainMenuHeader`) ne porte que l'avatar,
 * qui ouvre le compte, et un badge rouge « 1 » tant que l'e-mail n'est pas vérifié ; la barre
 * d'onglets du dessous n'a qu'un onglet. Ici : la marque à gauche (le lockup en grand écran, le
 * macaron sur un téléphone, comme chez le parent), la pastille de compte à droite — « Ma
 * candidature » en légende, le point quand l'e-mail attend — et pas de barre d'onglets.
 */
import React from "react"
import { useWindowDimensions, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Logo } from "../../atoms/Logo"
import { ProfilePill } from "../../molecules/ProfilePill"
import { breakpoints } from "../../theme/tokens/breakpoints"

const TUTOR_CANDIDATE_HEADER_TEXTS = {
    legend: "Ma candidature",
    account: "Ouvrir mon compte",
}

const WIDE_FROM = breakpoints.md
/** Les hauteurs du logo de l'en-tête parent : le lockup en grand, le macaron sur un téléphone. */
const LOGO_HEIGHT = { wide: 56, narrow: 30 }

export type TutorCandidateHeaderProps = {
    firstName: string
    avatarUri?: string | null
    /** L'e-mail n'est pas vérifié : un point sur l'avatar. */
    alert?: boolean
    onOpenAccount: () => void
    testID?: string
}

export function TutorCandidateHeader(props: TutorCandidateHeaderProps) {
    const { firstName, avatarUri, alert = false, onOpenAccount, testID } = props
    const { width } = useWindowDimensions()
    const wide = width >= WIDE_FROM
    const insets = useSafeAreaInsets()

    return (
        <View testID={testID} style={styles.root(insets.top)}>
            <Row align="center" justify="between" gap="3" style={styles.bar(wide)}>
                <Logo variant={wide ? "lockup" : "macaron"} height={wide ? LOGO_HEIGHT.wide : LOGO_HEIGHT.narrow} />
                <ProfilePill
                    name={firstName}
                    legend={TUTOR_CANDIDATE_HEADER_TEXTS.legend}
                    avatarUri={avatarUri ?? undefined}
                    alert={alert}
                    chevron={false}
                    onPress={onOpenAccount}
                    accessibilityLabel={TUTOR_CANDIDATE_HEADER_TEXTS.account}
                />
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: (topInset: number) => ({ paddingTop: topInset }),
    bar: (wide: boolean) => ({
        paddingHorizontal: wide ? theme.spacing["8"] : theme.spacing["3"],
        paddingVertical: wide ? theme.spacing["3"] : theme.spacing["2"],
    }),
}))
